import { MainSection } from "@/styles/global";
import React from "react";
import S from "./FormWibelfonden.styled";
import UploadIcon from "@/assets/upload.svg";
import axios from "axios";
import FormSuccess from "@/components/global-components/FormSuccess";
import { useForm } from "react-hook-form";

export interface FormWibelfondenProps {}

type FormData = {
  dovas_request_category: string;
  namn: string;
  personNummer: string;
  adress: string;
  postNummer: string;
  stad: string;
  email: string;
  telefon: string;
  utbildning: string;
  anstallning: string;
  soktBelopp: string;
  syfte: string;
  file?: File;
};

type FormState = {
  isLoading: boolean;
  error?: string;
  data: FormData;
  success: boolean;
};

const emptyForm = (): FormData => ({
  dovas_request_category: "Wibelfonden",
  namn: "",
  personNummer: "",
  stad: "",
  adress: "",
  email: "",
  telefon: "",
  utbildning: "",
  anstallning: "",
  syfte: "",
  postNummer: "",
  soktBelopp: "",
});

const emptyFormState = (): FormState => ({
  isLoading: false,
  data: emptyForm(),
  success: false,
});

const FormWibelfonden: React.FC<FormWibelfondenProps> = () => {
  const [formState, setFormState] = React.useState(emptyFormState());

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // update form data
  const updateFormProperty = <K extends keyof FormData>(
    property: K,
    value: FormData[K]
  ): void => {
    console.log(property, value);

    setFormState((state: FormState) => ({
      ...state,
      data: {
        ...state.data,
        [property]: value,
      },
    }));
  };

  const submitForm = handleSubmit(() => {
    console.log("submitting form", formState.data);

    setFormState((state) => ({
      ...state,
      isLoading: true,
    }));

    const formData = new FormData();

    (Object.keys(formState.data) as (keyof FormData)[]).forEach((key) => {
      if (key === "file" && formState.data.file) {
        formData.append(key, formState.data.file); // Lägg till filen
      } else {
        formData.append(key, formState.data[key] as string); // Lägg till övriga fält som strängar
      }
    });

    axios
      .post(process.env.GATSBY_CONTACT_URL || "", formData, {
        headers: {
          "Content-Type": "multipart/form-data", // Viktigt för att hantera filuppladdningar
        },
      })
      .then((res) => {
        if (!res.data.success) {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            error: res.data.error_msg,
          }));
        } else {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            success: true,
          }));
        }
      });
  });

  const removeFile = (type: string) => {
    return () => {
      if (type === "file") {
        setFormState((state) => ({
          ...state,
          data: {
            ...state.data,
            file: undefined,
          },
        }));
      }
    };
  };

  return (
    <MainSection>
      <S.Inner>
        {formState.success ? (
          <FormSuccess />
        ) : (
          <S.FormWrapper>
            <S.H4>
              Du ansöker till Stockholm Dövas Förening, Stiftelsen Margit
              Wibelfonden
            </S.H4>
            <S.Wrapper>
              <S.DetailsLarge>Obligatoriska fält</S.DetailsLarge>
            </S.Wrapper>
            <S.Form onSubmit={submitForm}>
              <S.Wrapper>
                <S.Label htmlFor="namn">Ditt namn</S.Label>
                <S.Input
                  type="text"
                  {...register("namn", { required: true })}
                  className={errors.namn ? "error" : ""}
                  placeholder="Ditt namn"
                  value={formState.data.namn}
                  onChange={(e) => updateFormProperty("namn", e.target.value)}
                />
                {errors.namn && (
                  <S.Error role="alert">Namn är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="personNummer">Personnummer</S.Label>
                <S.Input
                  type="text"
                  {...register("personNummer", { required: true })}
                  className={errors.personNummer ? "error" : ""}
                  placeholder="Personnummer"
                  onChange={(e) =>
                    updateFormProperty("personNummer", e.target.value)
                  }
                />
                {errors.personNummer && (
                  <S.Error role="alert">Personnummer är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="adress">Adress</S.Label>
                <S.Input
                  type="text"
                  {...register("adress", { required: true })}
                  className={errors.adress ? "error" : ""}
                  placeholder="Adress"
                  onChange={(e) => updateFormProperty("adress", e.target.value)}
                />
                {errors.adress && (
                  <S.Error role="alert">Adress är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.FlexWrapper>
                <S.Wrapper>
                  <S.Label htmlFor="postNummer">Postnummer</S.Label>
                  <S.Input
                    type="text"
                    {...register("postNummer", { required: true })}
                    className={errors.postNummer ? "error" : ""}
                    placeholder="Postnummer"
                    onChange={(e) =>
                      updateFormProperty("postNummer", e.target.value)
                    }
                  />
                  {errors.postNummer && (
                    <S.Error role="alert">Postnummer är obligatoriskt</S.Error>
                  )}
                </S.Wrapper>
                <S.Wrapper>
                  <S.Label htmlFor="stad">Postort</S.Label>
                  <S.Input
                    type="text"
                    {...register("stad", { required: true })}
                    className={errors.stad ? "error" : ""}
                    placeholder="Postort"
                    onChange={(e) => updateFormProperty("stad", e.target.value)}
                  />
                  {errors.stad && (
                    <S.Error role="alert">Postort är obligatoriskt</S.Error>
                  )}
                </S.Wrapper>
              </S.FlexWrapper>
              <S.Wrapper>
                <S.Label htmlFor="email">E-post</S.Label>
                <S.Input
                  type="email"
                  {...register("email", { required: true })}
                  className={errors.email ? "error" : ""}
                  placeholder="E-post"
                  onChange={(e) => updateFormProperty("email", e.target.value)}
                />
                {errors.email && (
                  <S.Error role="alert">E-post är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="telefon">Telefonnummer</S.Label>
                <S.Input
                  type="text"
                  {...register("telefon", { required: true })}
                  className={errors.telefon ? "error" : ""}
                  placeholder="Telefonnummer"
                  onChange={(e) =>
                    updateFormProperty("telefon", e.target.value)
                  }
                />
                {errors.telefon && (
                  <S.Error role="alert">Telefonnummer är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="utbildning">Utbildning</S.Label>
                <S.TextArea
                  {...register("utbildning", { required: true })}
                  className={errors.utbildning ? "error" : ""}
                  placeholder="Utbildning"
                  onChange={(e) =>
                    updateFormProperty("utbildning", e.target.value)
                  }
                ></S.TextArea>
                {errors.utbildning && (
                  <S.Error role="alert">Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="anstallning">Anställning</S.Label>
                <S.TextArea
                  {...register("anstallning", { required: true })}
                  className={errors.anstallning ? "error" : ""}
                  placeholder="Anställning"
                  onChange={(e) =>
                    updateFormProperty("anstallning", e.target.value)
                  }
                ></S.TextArea>
                {errors.anstallning && (
                  <S.Error role="alert">Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="soktBelopp">Sökt belopp</S.Label>
                <S.Input
                  {...register("soktBelopp", { required: true })}
                  className={errors.soktBelopp ? "error" : ""}
                  placeholder="Sökt belopp"
                  type="number"
                  onChange={(e) =>
                    updateFormProperty("soktBelopp", e.target.value)
                  }
                ></S.Input>
                {errors.soktBelopp && (
                  <S.Error role="alert">Sökt belopp är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="syfte">Beskriv ändamålet</S.Label>
                <S.TextArea
                  {...register("syfte", { required: true })}
                  className={errors.syfte ? "error" : ""}
                  placeholder="Ändamål"
                  onChange={(e) => updateFormProperty("syfte", e.target.value)}
                />
                {errors.syfte && (
                  <S.Error role="alert">Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label className="not-required">Bifoga filer</S.Label>
                <S.SmallLabel>Endast PDF</S.SmallLabel>
                <S.Attachments
                  htmlFor="file-upload"
                  className="custom-file-upload"
                >
                  Ladda upp från dator {<UploadIcon />}
                </S.Attachments>
                <S.UploadButton
                  name="file-upload"
                  id="file-upload"
                  type="file"
                  accept=".pdf"
                  onChange={(event) =>
                    updateFormProperty("file", (event.target.files || [])[0])
                  }
                />
                {formState.data.file && (
                  <S.FileWrapper>
                    <S.FileText>{formState.data.file.name}</S.FileText>
                    <svg
                      onClick={removeFile("file")}
                      width="15"
                      height="15"
                      viewBox="0 0 12 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.5 1.10786L10.3921 0L6 4.39214L1.60786 0L0.5 1.10786L4.89214 5.5L0.5 9.89214L1.60786 11L6 6.60786L10.3921 11L11.5 9.89214L7.10786 5.5L11.5 1.10786Z"
                        fill="#171131"
                      />
                    </svg>
                  </S.FileWrapper>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.CheckboxContainer>
                  <S.CheckboxWrapper>
                    <S.Checkbox
                      type="checkbox"
                      {...register("accept", { required: true })}
                      className={errors.accept ? "error" : ""}
                    />
                    <svg
                      width="15"
                      height="13"
                      viewBox="0 0 15 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.1667 2.52002L5.37504 10.3117L1.83337 6.77002"
                        stroke="#281E78"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </S.CheckboxWrapper>
                  <S.SmallLabel htmlFor="accept">
                    Jag godkänner Stockholm Dövas Förenings{" "}
                    <a href="/">integritetspolicy.</a>
                  </S.SmallLabel>
                </S.CheckboxContainer>
              </S.Wrapper>
              <S.Wrapper>
                <S.SubmitButton type="submit">Skicka</S.SubmitButton>
                {formState.error && (
                  <S.FormError>
                    Något gick fel, vänligen försök igen.
                  </S.FormError>
                )}
              </S.Wrapper>
            </S.Form>
          </S.FormWrapper>
        )}
      </S.Inner>
    </MainSection>
  );
};

export default FormWibelfonden;
